/**
 * AppRouteGuard
 *
 * Priority-based route guard that handles post-authentication routing decisions.
 * This component wraps protected routes and decides where users should be redirected
 * based on their state.
 *
 * ROUTING PRIORITY (in order):
 * 1. Pending Subscription → Stripe Checkout (must complete purchase flow first)
 * 2. Onboarding Incomplete → /onboarding/welcome (must complete onboarding)
 * 3. Otherwise → Requested route (normal access)
 *
 * CRITICAL: Pending subscription handling MUST take priority to preserve the
 * existing Stripe checkout flow. Breaking this flow would cause revenue loss.
 */

import { useEffect, useState, useRef, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';

interface AppRouteGuardProps {
  children: ReactNode;
}

// Pending subscription expiry threshold (24 hours)
const PENDING_SUBSCRIPTION_EXPIRY_MS = 24 * 60 * 60 * 1000;

export function AppRouteGuard({ children }: AppRouteGuardProps) {
  const { user, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [guardState, setGuardState] = useState<
    'checking' | 'pending_subscription' | 'onboarding' | 'allowed'
  >('checking');

  // Track if we've already checked for this user session
  const hasCheckedRef = useRef<string | null>(null);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    // If no user, the parent route guard should handle redirect to /auth
    if (!user) {
      setGuardState('allowed');
      return;
    }

    // If we've already checked for this user, don't re-check
    // (Prevents infinite loops and unnecessary API calls)
    if (hasCheckedRef.current === user.id) {
      return;
    }

    // Skip checking if we're already on onboarding routes
    if (location.pathname.startsWith('/onboarding')) {
      setGuardState('allowed');
      return;
    }

    const performRouteGuardCheck = async () => {
      logger.info('[AppRouteGuard] Checking route access for user', { userId: user.id });

      try {
        // ===================================================================
        // PRIORITY 1: Check for pending subscription (MUST BE FIRST)
        // ===================================================================

        // Only check pending subscription if NOT coming from email confirmation
        // The usePendingSubscription hook handles email confirmation flow
        const searchParams = new URLSearchParams(location.search);
        const isEmailConfirmationTab = searchParams.get('source') === 'email-confirmation';

        if (!isEmailConfirmationTab) {
          // Check for unclaimed pending subscription
          const { data: pendingSubscription, error: pendingError } = await supabase
            .from('pending_subscriptions')
            .select('plan, interval, created_at')
            .eq('user_id', user.id)
            .is('claimed_at', null)
            .maybeSingle();

          if (!pendingError && pendingSubscription) {
            // Check if pending subscription is expired (older than 24 hours)
            const createdAt = new Date(pendingSubscription.created_at).getTime();
            const now = Date.now();
            const isExpired = now - createdAt > PENDING_SUBSCRIPTION_EXPIRY_MS;

            if (!isExpired) {
              logger.info('[AppRouteGuard] Found active pending subscription, redirecting to pricing', {
                plan: pendingSubscription.plan,
                interval: pendingSubscription.interval,
              });

              // Mark as checked to prevent re-checking
              hasCheckedRef.current = user.id;
              setGuardState('pending_subscription');

              // Redirect to pricing with plan params (Stripe checkout will be triggered there)
              navigate(
                `/virtual-staging/pricing?plan=${pendingSubscription.plan}&interval=${pendingSubscription.interval}`,
                { replace: true }
              );
              return;
            } else {
              // Pending subscription expired, clean it up
              logger.info('[AppRouteGuard] Pending subscription expired, cleaning up');
              await supabase
                .from('pending_subscriptions')
                .update({ claimed_at: new Date().toISOString() })
                .eq('user_id', user.id)
                .is('claimed_at', null);
            }
          }
        }

        // ===================================================================
        // PRIORITY 2: Check for onboarding completion
        // ===================================================================

        // Wait for profile to be loaded
        if (!profile) {
          // Profile not loaded yet, wait for it
          logger.info('[AppRouteGuard] Waiting for profile to load...');
          return;
        }

        // Check onboarding_completed flag
        // Default to true if field doesn't exist (for backwards compatibility with existing users)
        const onboardingCompleted = profile.onboarding_completed ?? true;

        if (!onboardingCompleted) {
          logger.info('[AppRouteGuard] Onboarding not completed, redirecting to onboarding');

          // Mark as checked to prevent re-checking
          hasCheckedRef.current = user.id;
          setGuardState('onboarding');

          // Redirect to onboarding
          navigate('/onboarding/welcome', { replace: true });
          return;
        }

        // ===================================================================
        // PRIORITY 3: Allow access to requested route
        // ===================================================================

        logger.info('[AppRouteGuard] All checks passed, allowing access');
        hasCheckedRef.current = user.id;
        setGuardState('allowed');
      } catch (error) {
        logger.error('[AppRouteGuard] Error during route guard check', error);
        // On error, allow access to prevent blocking users
        // Better to show dashboard with issues than block completely
        hasCheckedRef.current = user.id;
        setGuardState('allowed');
      }
    };

    performRouteGuardCheck();
  }, [user, profile, authLoading, navigate, location.pathname, location.search]);

  // Reset check state when user changes (e.g., logout and login as different user)
  useEffect(() => {
    if (!user) {
      hasCheckedRef.current = null;
      setGuardState('checking');
    }
  }, [user]);

  // Show loading state while checking
  if (guardState === 'checking' || authLoading) {
    return (
      <div className="min-h-screen bg-alabaster flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-olive border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sand font-sans">Loading...</p>
        </div>
      </div>
    );
  }

  // For pending_subscription and onboarding states, the navigate() has been called
  // but we still need to wait for navigation to complete
  if (guardState === 'pending_subscription' || guardState === 'onboarding') {
    return (
      <div className="min-h-screen bg-alabaster flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-olive border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sand font-sans">
            {guardState === 'pending_subscription'
              ? 'Completing your subscription...'
              : 'Preparing your experience...'}
          </p>
        </div>
      </div>
    );
  }

  // guardState === 'allowed' - render children
  return <>{children}</>;
}

/**
 * Hook to check if user needs onboarding
 * Can be used by components to conditionally show onboarding-related UI
 */
export function useNeedsOnboarding(): boolean {
  const { profile } = useAuth();

  if (!profile) return false;

  // Return true if onboarding is not completed
  return profile.onboarding_completed === false;
}

/**
 * Hook to get onboarding completion status
 */
export function useOnboardingStatus() {
  const { profile } = useAuth();

  return {
    isCompleted: profile?.onboarding_completed ?? true,
    completedAt: profile?.onboarding_completed_at ?? null,
    segment: profile?.user_segment ?? null,
    data: profile?.onboarding_data ?? {},
  };
}
